import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import DocumentUpload from "@/components/app/DocumentUpload";
import { Plus, Pencil, Trash2, Eye, MessageSquare } from "lucide-react";

export const LISTING_STATUSES = ["draft", "pending_review", "active", "pending_offer", "under_contract", "sold", "withdrawn"];

const STATUS_LABEL = {
  draft: "Draft", pending_review: "Pending Review", active: "Active",
  pending_offer: "Pending Offer", under_contract: "Under Contract",
  sold: "Sold", withdrawn: "Withdrawn",
};
const STATUS_STYLE = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending_offer: "bg-blue-50 text-blue-700 border-blue-200",
  under_contract: "bg-purple-50 text-purple-700 border-purple-200",
  sold: "bg-slate-100 text-slate-700 border-slate-300",
  withdrawn: "bg-red-50 text-red-700 border-red-200",
};

const EMPTY_FORM = {
  category: "performing", grade: "B",
  vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_mileage: "",
  state: "", original_loan_amount: "", remaining_balance: "",
  interest_rate: "", weekly_payment: "", remaining_term_weeks: "",
  payments_made: 0, late_payments: 0,
  asking_price: "",
  payment_frequency: "monthly", description: "",
};

export function ListingStatusPill({ status }) {
  return (
    <span className={`px-2 py-0.5 border text-[10px] font-overline ${STATUS_STYLE[status] || "bg-gray-100"}`}
      data-testid={`listing-status-${status}`}>{STATUS_LABEL[status] || status}</span>
  );
}

function ListingForm({ initial, onSubmit, busy }) {
  const [f, setF] = useState(initial || EMPTY_FORM);
  const [preview, setPreview] = useState({ estimated_yield: 0, annual_cashflow: 0, remaining_term_months: 0 });
  useEffect(() => { setF(initial || EMPTY_FORM); }, [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setNum = (k) => (e) => setF({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) });

  // Live server-computed yield preview — FinanceLocal owns the calculation.
  useEffect(() => {
    const pay = Number(f.weekly_payment), term = Number(f.remaining_term_weeks), ask = Number(f.asking_price);
    if (!pay || !term || !ask) { setPreview({ estimated_yield: 0, annual_cashflow: 0, remaining_term_months: 0 }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/seller/yield-preview", {
          weekly_payment: pay, remaining_term_weeks: term,
          asking_price: ask, payment_frequency: f.payment_frequency || "weekly",
        });
        setPreview(r.data);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [f.weekly_payment, f.remaining_term_weeks, f.asking_price, f.payment_frequency]);

  const freq = f.payment_frequency || "weekly";
  const termLabel  = freq === "monthly" ? "Term (months)" : freq === "biweekly" ? "Term (bi-weeks)" : "Term (weeks)";
  const paymentLabel = freq === "monthly" ? "Monthly Payment $" : freq === "biweekly" ? "Bi-weekly Payment $" : "Weekly Payment $";

  const submit = () => {
    const req = ["vehicle_year","vehicle_make","vehicle_model","state","original_loan_amount","remaining_balance","interest_rate","weekly_payment","remaining_term_weeks","asking_price"];
    for (const k of req) {
      if (f[k] === "" || f[k] == null) { toast.error(`Missing: ${k.replace(/_/g," ")}`); return; }
    }
    onSubmit({
      ...f,
      vehicle_year: Number(f.vehicle_year), vehicle_mileage: Number(f.vehicle_mileage || 0),
      original_loan_amount: Number(f.original_loan_amount), remaining_balance: Number(f.remaining_balance),
      interest_rate: Number(f.interest_rate), weekly_payment: Number(f.weekly_payment),
      remaining_term_weeks: Number(f.remaining_term_weeks),
      payments_made: Number(f.payments_made || 0), late_payments: Number(f.late_payments || 0),
      // estimated_yield is computed server-side; we still send the preview for backward compat but backend overrides.
      estimated_yield: Number(preview.estimated_yield || 0),
      asking_price: Number(f.asking_price),
      state: (f.state || "").toUpperCase(),
    });
  };
  return (
    <div className="space-y-4" data-testid="listing-form">
      <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-xs" data-testid="listing-review-notice">
        <b>Pending Review:</b> All new listings are reviewed by a FinanceLocal admin before going live on the marketplace. Review usually takes less than 1 business day. You'll be notified when it's approved.
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        <Lab label="Category"><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="performing">Performing</SelectItem>
            <SelectItem value="semi_performing">Semi-Performing</SelectItem>
            <SelectItem value="non_performing">Non-Performing</SelectItem>
            <SelectItem value="repo_ready">Repo Ready</SelectItem>
            <SelectItem value="fresh_origination">Fresh Origination</SelectItem>
          </SelectContent></Select></Lab>
        <Lab label="Grade"><Select value={f.grade} onValueChange={(v) => setF({ ...f, grade: v })}>
          <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {["A","B","C","D"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent></Select></Lab>
        <Lab label="State (2-letter)"><Input className="rounded-none" value={f.state} onChange={set("state")} maxLength={2} placeholder="TX" data-testid="lf-state" /></Lab>
        <Lab label="Year"><Input type="number" className="rounded-none" value={f.vehicle_year} onChange={setNum("vehicle_year")} data-testid="lf-year" /></Lab>
        <Lab label="Make"><Input className="rounded-none" value={f.vehicle_make} onChange={set("vehicle_make")} data-testid="lf-make" /></Lab>
        <Lab label="Model"><Input className="rounded-none" value={f.vehicle_model} onChange={set("vehicle_model")} data-testid="lf-model" /></Lab>
        <Lab label="Mileage"><Input type="number" className="rounded-none" value={f.vehicle_mileage} onChange={setNum("vehicle_mileage")} /></Lab>
        <Lab label="Original Loan $"><Input type="number" className="rounded-none" value={f.original_loan_amount} onChange={setNum("original_loan_amount")} /></Lab>
        <Lab label="Remaining $"><Input type="number" className="rounded-none" value={f.remaining_balance} onChange={setNum("remaining_balance")} /></Lab>
        <Lab label="Interest %"><Input type="number" className="rounded-none" value={f.interest_rate} onChange={setNum("interest_rate")} /></Lab>
        <Lab label="Payment Frequency"><Select value={f.payment_frequency} onValueChange={(v) => setF({ ...f, payment_frequency: v })}>
          <SelectTrigger className="rounded-none" data-testid="lf-payment-frequency"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="biweekly">Bi-weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent></Select></Lab>
        <Lab label={paymentLabel}><Input type="number" className="rounded-none" value={f.weekly_payment} onChange={setNum("weekly_payment")} data-testid="lf-payment-amount" /></Lab>
        <Lab label={termLabel}><Input type="number" className="rounded-none" value={f.remaining_term_weeks} onChange={setNum("remaining_term_weeks")} data-testid="lf-term" /></Lab>
        <Lab label="Payments Made"><Input type="number" className="rounded-none" value={f.payments_made} onChange={setNum("payments_made")} /></Lab>
        <Lab label="Late Payments"><Input type="number" className="rounded-none" value={f.late_payments} onChange={setNum("late_payments")} /></Lab>
        <Lab label="Asking Price $"><Input type="number" className="rounded-none" value={f.asking_price} onChange={setNum("asking_price")} data-testid="lf-asking" /></Lab>
      </div>

      {/* FinanceLocal-computed preview — read only */}
      <div className="border border-border p-3 grid grid-cols-3 gap-3 bg-muted/20" data-testid="lf-yield-preview">
        <div>
          <div className="font-overline text-[10px] text-muted-foreground">Estimated Yield (calculated)</div>
          <div className="font-mono-data text-2xl" data-testid="lf-yield-value">{preview.estimated_yield ? `${preview.estimated_yield}%` : "—"}</div>
        </div>
        <div>
          <div className="font-overline text-[10px] text-muted-foreground">Projected Annual Cash Flow</div>
          <div className="font-mono-data text-2xl" data-testid="lf-cashflow-value">{preview.annual_cashflow ? `$${Number(preview.annual_cashflow).toLocaleString()}` : "—"}</div>
        </div>
        <div>
          <div className="font-overline text-[10px] text-muted-foreground">Remaining Term</div>
          <div className="font-mono-data text-2xl">
            {Number(f.remaining_term_weeks) > 0 ? `${f.remaining_term_weeks} ${freq === "monthly" ? "mo" : freq === "biweekly" ? "biwk" : "wk"}` : "—"}
          </div>
        </div>
        <div className="col-span-3 text-[10px] text-muted-foreground">
          FinanceLocal calculates yield and annual cash flow from your entered payment frequency, payment amount, term, and asking price. Enter accurate values for the best listing quality.
        </div>
      </div>

      <Lab label="Description"><Textarea rows={3} className="rounded-none" value={f.description} onChange={set("description")} /></Lab>
      <Button onClick={submit} disabled={busy} className="rounded-none uppercase tracking-wider"
        data-testid="lf-submit">{busy ? "Saving…" : initial ? "Save Changes" : "Submit Listing"}</Button>
    </div>
  );
}
function Lab({ label, children }) {
  return <div><Label className="font-overline text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function SellerListings({ onMessagesClick }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [offersFor, setOffersFor] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/seller/notes/mine");
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    setBusy(true);
    try {
      await api.post("/seller/notes", payload);
      toast.success("Listing submitted for admin review");
      setCreating(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const save = async (payload) => {
    setBusy(true);
    try {
      const r = await api.patch(`/seller/notes/${editing.note_id}`, payload);
      toast.success(r.data.bounced_back_to_review ? "Changes saved — listing returned to review" : "Changes saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const withdraw = async (n) => {
    if (!window.confirm("Withdraw this listing? Any pending offers will be closed.")) return;
    try {
      const r = await api.post(`/seller/notes/${n.note_id}/withdraw`);
      toast.success(`Withdrawn. ${r.data.closed_offers || 0} offer(s) auto-closed.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const shown = filter === "all" ? items : items.filter((n) => (n.listing_status || "draft") === filter);

  return (
    <div className="space-y-6" data-testid="seller-listings">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={() => setFilter("all")}
            className={`px-3 py-1 border text-xs font-overline ${filter==="all" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
            data-testid="filter-all">All ({items.length})</button>
          {LISTING_STATUSES.map((s) => {
            const n = items.filter((i) => (i.listing_status || "draft") === s).length;
            return (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-3 py-1 border text-xs font-overline ${filter===s ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                data-testid={`filter-${s}`}>{STATUS_LABEL[s]} {n > 0 && `(${n})`}</button>
            );
          })}
        </div>
        <Button onClick={() => setCreating(true)} className="rounded-none uppercase text-xs tracking-wider"
          data-testid="seller-create-listing">
          <Plus className="w-4 h-4 mr-1" /> New Listing
        </Button>
      </div>

      {shown.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground"
          data-testid="seller-listings-empty">
          <div>No listings here yet.</div>
          {filter === "all" && (
            <Button onClick={() => setCreating(true)} className="mt-4 rounded-none uppercase text-xs tracking-wider">
              Create your first listing
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left">
              <tr>
                <Th>Vehicle</Th><Th>Status</Th><Th>Offers</Th><Th>Asking</Th><Th>Yield</Th><Th>Created</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {shown.map((n) => {
                const st = n.listing_status || "draft";
                const editable = ["draft","pending_review","active"].includes(st);
                const canWithdraw = !["sold","withdrawn"].includes(st);
                return (
                  <tr key={n.note_id} className="border-t border-border" data-testid={`listing-row-${n.note_id}`}>
                    <Td>{n.vehicle_year} {n.vehicle_make} {n.vehicle_model}</Td>
                    <Td><ListingStatusPill status={st} /></Td>
                    <Td className="font-mono-data">
                      <button onClick={() => setOffersFor(n)} className="hover:text-primary underline-offset-2 hover:underline"
                        data-testid={`view-offers-${n.note_id}`}>
                        {n.offers_summary?.pending || 0}P / {n.offers_summary?.countered || 0}C / {n.offers_summary?.accepted || 0}A
                      </button>
                    </Td>
                    <Td className="font-mono-data">{fmtMoney(n.asking_price)}</Td>
                    <Td className="font-mono-data">{fmtPct(n.estimated_yield)}</Td>
                    <Td className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</Td>
                    <Td>
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                          onClick={() => setOffersFor(n)} data-testid={`offers-btn-${n.note_id}`}>
                          <MessageSquare className="w-3 h-3 mr-1" /> Offers
                        </Button>
                        {editable && (
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                            onClick={() => setEditing(n)} data-testid={`edit-btn-${n.note_id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        {canWithdraw && (
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                            onClick={() => withdraw(n)} data-testid={`withdraw-btn-${n.note_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New Listing</DialogTitle>
            <DialogDescription>All new listings require admin moderation before going live.</DialogDescription>
          </DialogHeader>
          <ListingForm onSubmit={create} busy={busy} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit Listing</DialogTitle>
            <DialogDescription>Editing an active listing will return it to admin review.</DialogDescription>
          </DialogHeader>
          {editing && (
            <>
              <ListingForm initial={editing} onSubmit={save} busy={busy} />
              <div className="border-t border-border pt-4 mt-4">
                <DocumentUpload
                  entityType="note"
                  entityId={editing.note_id}
                  title="Note Documents"
                  docTypes={[
                    { value: "vehicle_photo", label: "Vehicle Photo" },
                    { value: "ric", label: "Retail Installment Contract" },
                    { value: "payment_history", label: "Payment History" },
                    { value: "title", label: "Title" },
                    { value: "insurance", label: "Insurance" },
                    { value: "inspection", label: "Inspection" },
                    { value: "additional", label: "Additional Supporting Documents" },
                  ]}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <SellerOffersDialog
        note={offersFor}
        open={!!offersFor}
        onClose={() => { setOffersFor(null); load(); }}
        onMessagesClick={onMessagesClick}
      />
    </div>
  );
}

// ---- Offers inbox modal ----
function SellerOffersDialog({ note, open, onClose, onMessagesClick }) {
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [countering, setCountering] = useState(null);
  const [counterAmt, setCounterAmt] = useState("");
  const [counterMsg, setCounterMsg] = useState("");

  const load = async () => {
    if (!note) return;
    try {
      const r = await api.get(`/seller/notes/${note.note_id}/offers`);
      setOffers(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { if (open) load(); }, [open, note?.note_id]);

  const act = async (id, verb, payload) => {
    setBusy(true);
    try {
      const r = await api.post(`/offers/${id}/${verb}`, payload || {});
      if (verb === "accept" && r.data.auto_closed_count) {
        toast.success(`Accepted. ${r.data.auto_closed_count} other offer(s) auto-closed.`);
      } else {
        toast.success(`Offer ${verb}ed`);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitCounter = async () => {
    if (!counterAmt || isNaN(Number(counterAmt))) { toast.error("Counter amount required"); return; }
    await act(countering.offer_id, "counter", { counter_amount: Number(counterAmt), message: counterMsg });
    setCountering(null); setCounterAmt(""); setCounterMsg("");
  };

  if (!note) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="seller-offers-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Offers · {note.vehicle_year} {note.vehicle_make} {note.vehicle_model}
          </DialogTitle>
          <DialogDescription>Accept, counter, or reject each offer. Accepting one auto-closes the others.</DialogDescription>
        </DialogHeader>
        {offers.length === 0 ? (
          <div className="text-muted-foreground text-sm py-6 text-center" data-testid="no-offers-msg">
            No offers yet.
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {offers.map((o) => {
              const isOpen = ["pending","countered"].includes(o.status);
              return (
                <div key={o.offer_id} className="p-4" data-testid={`offer-${o.offer_id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-mono-data text-sm">{o.investor_name}</div>
                      <div className="text-xs text-muted-foreground">{o.investor_email}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Status: <span className="font-overline">{o.status}</span> · {new Date(o.created_at).toLocaleString()}
                      </div>
                      {o.message && <div className="mt-2 text-sm italic">&quot;{o.message}&quot;</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-mono-data text-2xl text-primary">{fmtMoney(o.offer_amount)}</div>
                      {o.counter_amount && (
                        <div className="text-xs text-muted-foreground">Counter: {fmtMoney(o.counter_amount)}</div>
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {o.status === "pending" && (
                        <>
                          <Button size="sm" className="rounded-none text-xs h-8" disabled={busy}
                            onClick={() => act(o.offer_id, "accept")} data-testid={`accept-${o.offer_id}`}>Accept</Button>
                          <Button size="sm" variant="outline" className="rounded-none text-xs h-8" disabled={busy}
                            onClick={() => { setCountering(o); setCounterAmt(o.offer_amount); setCounterMsg(""); }}
                            data-testid={`counter-${o.offer_id}`}>Counter</Button>
                        </>
                      )}
                      <Button size="sm" variant="destructive" className="rounded-none text-xs h-8" disabled={busy}
                        onClick={() => act(o.offer_id, "reject")} data-testid={`reject-${o.offer_id}`}>Reject</Button>
                      {onMessagesClick && (
                        <Button size="sm" variant="outline" className="rounded-none text-xs h-8"
                          onClick={() => onMessagesClick(o.offer_id)} data-testid={`message-${o.offer_id}`}>
                          <MessageSquare className="w-3 h-3 mr-1" /> Message
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} variant="outline" className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!countering} onOpenChange={(v) => !v && setCountering(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="counter-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Send Counter</DialogTitle>
            <DialogDescription>Propose a different price and add an optional message to the buyer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Lab label="Counter Amount $">
              <Input type="number" className="rounded-none" value={counterAmt}
                onChange={(e) => setCounterAmt(e.target.value)} data-testid="counter-amount" />
            </Lab>
            <Lab label="Message (optional)">
              <Textarea rows={3} className="rounded-none" value={counterMsg}
                onChange={(e) => setCounterMsg(e.target.value)} data-testid="counter-message" />
            </Lab>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setCountering(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={submitCounter} disabled={busy}
              data-testid="counter-submit">{busy ? "…" : "Send Counter"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 font-overline text-muted-foreground text-xs">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
